import { useMemo } from 'react';
import { ProfilesDaoSimProfileDetails } from 'types/__generated__/SimInventoryApi';
import { SingleLegacyPlan } from 'types/legacyDashboard';

interface UseCoverageProfilesProps {
  simProfiles?: ProfilesDaoSimProfileDetails[];
  planProfiles?: SingleLegacyPlan['profiles'];
}

export type CoverageSearchProfiles = {
  profileName: string;
  installationState: string | null;
  offerId: number;
};

/**
 * We can either display coverage by SIM or by plan.
 * The shape of SIM profiles is slightly different from the shape of plan profiles.
 * This hook normalizes the shape of the profiles so that we can display them in the same way.
 * @param simProfiles - The list of SIM profiles.
 * @param planProfiles - The list of plan profiles.
 * @returns A list of profiles for the coverage search.
 */

const useCoverageProfiles = ({
  simProfiles,
  planProfiles,
}: UseCoverageProfilesProps): CoverageSearchProfiles[] | null => {
  const profiles = useMemo(() => {
    if (simProfiles && simProfiles.length > 0) {
      return simProfiles.map((profile) => ({
        profileName: profile.profileName ?? '',
        installationState: profile.installationState ?? null,
        offerId: profile.offerId ?? 0,
      }));
    }
    if (planProfiles && planProfiles.length > 0) {
      return planProfiles.map((profile) => ({
        profileName: profile.name,
        installationState: null,
        offerId: profile.offerId,
      }));
    }
    return null;
  }, [simProfiles, planProfiles]);

  return profiles;
};

export default useCoverageProfiles;
